import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import { getUserToken } from "utils/helperFunctions";
import type {
  CheckInSummary,
  CriticalIssue,
  DismissIssueParams,
  GetCheckInSummaryParams,
  GetCriticalIssuesParams,
  GetHighestValueConversationsParams,
  GetMondayMorningWrapUpParams,
  GetTeamCheckInUsersBySubjectParams,
  HighestValueConversation,
  MondayMorningWrapUp,
  TeamCheckInUserItem,
} from "./types";
import { MIN_VISIBLE_CONCERN_LEVEL } from "./helpers";

// Team listing endpoints use stale-while-revalidate: cached data is shown immediately
// while a background refetch runs silently. This only works correctly because the
// skeleton condition uses isFetching (not isLoading) — if that ever changes to isLoading,
// users will see stale data with no visual feedback for the full cache window.
const TEAM_LISTING_CACHE_SECONDS = 300;

function toQuerySuffix(qs: string): string {
  return qs ? `?${qs}` : "";
}

/* --------------------------------------------------------
  API SLICE
-------------------------------------------------------- */

export const teamsCheckInApi = createApi({
  reducerPath: "teamsCheckInApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${REACT_APP_API_URL}/talentInsights`,
    prepareHeaders: (headers) => {
      const token = getUserToken();
      if (token) {
        headers.set("Authorization", token);
      }
      return headers;
    },
  }),
  tagTypes: ["CriticalIssue", "CriticalIssueList"],
  endpoints: (builder) => ({
    /* ---- Phase 1 ---- */

    getCriticalIssues: builder.query<CriticalIssue[], GetCriticalIssuesParams>({
      query: (params) => {
        const urlParams = new URLSearchParams();
        if (params.date !== undefined) {
          urlParams.append("date", params.date);
        }
        if (params.allTime !== undefined) {
          urlParams.append("allTime", String(params.allTime));
        }
        if (params.showDismissed !== undefined) {
          urlParams.append("showDismissed", String(params.showDismissed));
        }
        const qs = urlParams.toString();
        return `weeklyCheckInCriticalIssues${toQuerySuffix(qs)}`;
      },
      // Filtered here so the issue list and the header item counts stay in sync.
      // Unscored (null) issues are hidden; a missing field means an older API, so keep everything.
      transformResponse: (issues: CriticalIssue[]) =>
        issues.filter(
          (issue) =>
            issue.concernLevel === undefined ||
            (issue.concernLevel ?? 0) >= MIN_VISIBLE_CONCERN_LEVEL
        ),
      providesTags: (result) => {
        if (!result) return [{ type: "CriticalIssueList" as const }];
        return [
          { type: "CriticalIssueList" as const },
          ...result.map((issue) => ({
            type: "CriticalIssue" as const,
            id: issue.threadId,
          })),
        ];
      },
    }),

    dismissIssue: builder.mutation<CriticalIssue, DismissIssueParams>({
      query: ({ threadId, isDismissed }) => ({
        url: `weeklyCheckInCriticalIssues/${threadId}/dismiss`,
        method: "PUT",
        body: { isDismissed },
      }),
      async onQueryStarted(
        { threadId, isDismissed },
        { dispatch, queryFulfilled }
      ) {
        if (!isDismissed) return;
        const patch = dispatch(
          teamsCheckInApi.util.updateQueryData(
            "getCriticalIssues",
            { showDismissed: false },
            (draft) => {
              const idx = draft.findIndex(
                (issue) => issue.threadId === threadId
              );
              if (idx !== -1) draft.splice(idx, 1);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_, __, { threadId }) => [
        { type: "CriticalIssue", id: threadId },
        { type: "CriticalIssueList" },
      ],
    }),

    /* ---- Phase 2 (defined, not yet wired in UI) ---- */

    getMondayMorningWrapUp: builder.query<
      MondayMorningWrapUp,
      GetMondayMorningWrapUpParams
    >({
      query: (params) => {
        const urlParams = new URLSearchParams();
        if (params.date !== undefined) {
          urlParams.append("date", params.date);
        }
        const qs = urlParams.toString();
        return `mondayMorningWrapUp${toQuerySuffix(qs)}`;
      },
    }),

    getHighestValueConversations: builder.query<
      HighestValueConversation[],
      GetHighestValueConversationsParams
    >({
      query: (params) => {
        const urlParams = new URLSearchParams();
        if (params.date !== undefined) {
          urlParams.append("date", params.date);
        }
        const qs = urlParams.toString();
        return `highestValueConversations${toQuerySuffix(qs)}`;
      },
    }),

    getCheckInSummary: builder.query<CheckInSummary, GetCheckInSummaryParams>({
      query: ({ userAccountId, date, mostRecentCompletedCheckIn }) => {
        const urlParams = new URLSearchParams();
        if (date) {
          urlParams.append("date", date);
        }
        if (mostRecentCompletedCheckIn !== undefined) {
          urlParams.append(
            "mostRecentCompletedCheckIn",
            String(mostRecentCompletedCheckIn)
          );
        }
        const qs = urlParams.toString();
        return `getCheckInSummary/${userAccountId}${toQuerySuffix(qs)}`;
      },
    }),

    /* ---- DBC-616 / DBC-624 ---- */

    getTeamCheckInUsersBySubject: builder.query<
      TeamCheckInUserItem[],
      GetTeamCheckInUsersBySubjectParams
    >({
      keepUnusedDataFor: TEAM_LISTING_CACHE_SECONDS,
      query: ({
        subjectUserAccountId,
        startDate,
        endDate,
        checkInStatus,
        status,
        sortBy,
        sortDirection,
        searchString,
      }) => {
        const urlParams = new URLSearchParams();
        if (startDate) urlParams.append("startDate", startDate);
        if (startDate && endDate) urlParams.append("endDate", endDate);
        if (checkInStatus && checkInStatus !== "all")
          urlParams.append("checkInStatus", checkInStatus);
        if (status && status !== "all") urlParams.append("status", status);
        if (sortBy) urlParams.append("sortBy", sortBy);
        if (sortBy && sortDirection)
          urlParams.append("sortDirection", sortDirection);
        if (searchString) urlParams.append("searchString", searchString);
        const qs = urlParams.toString();
        return `teamCheckInUsers/${subjectUserAccountId}${toQuerySuffix(qs)}`;
      },
    }),
  }),
});

/* --------------------------------------------------------
  EXPORT HOOKS
-------------------------------------------------------- */

export const {
  // Phase 1 — wired in UI
  useGetCriticalIssuesQuery,
  useDismissIssueMutation,

  // Phase 2 — defined, not yet used
  useGetMondayMorningWrapUpQuery,
  useGetHighestValueConversationsQuery,
  useGetCheckInSummaryQuery,

  // DBC-616 / DBC-624 — My Teams tab
  useGetTeamCheckInUsersBySubjectQuery,
} = teamsCheckInApi;
